import {
  addHours,
  format,
  isSameDay,
  max,
  min,
  parse,
  setHours,
  setMinutes,
} from 'date-fns'
import type { AdhocBookingBookedTransportation } from '@/types/booking'

export function calculateJourneyDate(
  transportations: Array<AdhocBookingBookedTransportation>,
  formatPattern = 'EEE, dd MMMM yyyy',
): string | null {
  const dates: Array<Date> = []

  for (const transportation of transportations) {
    if (!transportation.departure_date) continue
    const departureDateAt = parse(
      transportation.departure_date,
      'yyyy-MM-dd',
      new Date(),
    )
    if (transportation.booked_transportation_hourly?.pickup_time) {
      const departureAt = parse(
        transportation.booked_transportation_hourly.pickup_time,
        'hh:mm a',
        departureDateAt,
      )
      dates.push(departureAt)
      const returnAt = addHours(
        departureAt,
        transportation.booked_transportation_hourly.duration,
      )
      const inSameDay = isSameDay(departureAt, returnAt)
      if (!inSameDay) dates.push(returnAt)
    }
    if (transportation.booked_transportation_point_to_points?.length) {
      for (const point of transportation.booked_transportation_point_to_points) {
        if (point.pickup_time) {
          dates.push(parse(point.pickup_time, 'hh:mm a', departureDateAt))
        }
      }
    }
  }

  if (!dates.length) return null

  dates.sort((a, b) => a.getTime() - b.getTime())

  const minDate = min(dates)
  const maxDate = max(dates)

  const inSameDay = isSameDay(minDate, maxDate)

  const formattedMin = format(minDate, formatPattern)
  const formattedMax = format(maxDate, formatPattern)

  return inSameDay ? formattedMin : `${formattedMin} - ${formattedMax}`
}

export function getMonthNames(
  locale = 'en-US',
  monthFormat: 'numeric' | '2-digit' | 'long' | 'short' | 'narrow' = 'long',
) {
  return Array.from({ length: 12 }, (_, i) =>
    new Intl.DateTimeFormat(locale, { month: monthFormat }).format(
      new Date(2000, i, 1),
    ),
  )
}

export function getWeekdayNames(
  locale = 'en-US',
  weekdayFormat: 'long' | 'short' | 'narrow' = 'short',
  weekStart = 0,
) {
  return Array.from({ length: 7 }, (_, i) => {
    const day = (i + weekStart) % 7
    return new Intl.DateTimeFormat(locale, { weekday: weekdayFormat }).format(
      new Date(2000, 0, 2 + day),
    )
  })
}

export function toTimeSlot(time: string) {
  return format(parse(time, 'hh:mm aa', new Date()), 'HH:00')
}

/**
 * Returns the nearest available time slot.
 *
 * - Same day → round to hour, but if minutes ≥ 55 → next hour
 * - Future or past day → "07:00"
 */
export function toNearestTimeSlot(
  selectedDate: Date = new Date(),
  currentDate: Date = new Date(),
): string {
  if (isSameDay(selectedDate, currentDate)) {
    const hours = currentDate.getHours()
    const minutes = currentDate.getMinutes()

    const adjustedHour = minutes >= 55 ? hours + 1 : hours
    const hour = adjustedHour >= 24 ? 23 : adjustedHour

    return `${String(hour).padStart(2, '0')}:00`
  }

  return '07:00'
}

/**
 * Ceil the current time to the nearest 5-minute slot, with optional hour offset.
 *
 * - Same day → ceil to next 5 minutes
 * - Future/past day → "07:00"
 * - `addHoursOffset` → adjust final slot by +N or -N hours
 *
 * @example
 * ceilToNearestFiveMinutes(selected, current) → "12:25"
 * ceilToNearestFiveMinutes(selected, current, 1) → "13:25"
 */
export function ceilToNearestFiveMinutes(
  selectedDate: Date = new Date(),
  currentDate: Date = new Date(),
  addHoursOffset = 0,
): string {
  if (!isSameDay(selectedDate, currentDate))
    return format(selectedDate, 'HH:mm')

  const minutes = currentDate.getMinutes()
  const hours = currentDate.getHours()

  let nextMinutes = minutes % 5 === 0 ? minutes : Math.ceil(minutes / 5) * 5
  let nextHour = hours

  if (nextMinutes === 60) {
    nextMinutes = 0
    nextHour += 1
  }

  nextHour += addHoursOffset

  if (nextHour >= 24) {
    nextHour = 23
    nextMinutes = 55
  } else if (nextHour < 0) {
    nextHour = 0
    nextMinutes = 0
  }

  const rounded = setMinutes(setHours(currentDate, nextHour), nextMinutes)
  return format(rounded, 'HH:mm')
}
